import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analyticsService';

const elementHref = (element) => {
  const href = element.getAttribute('href');
  if (!href) return undefined;

  try {
    const parsed = new URL(href, window.location.origin);
    return parsed.origin === window.location.origin
      ? parsed.pathname
      : `${parsed.hostname}${parsed.pathname}`;
  } catch {
    return href.split('?')[0];
  }
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });

    analyticsService.trackPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    const handleClick = (event) => {
      if (window.location.pathname.startsWith('/admin')) return;
      if (!(event.target instanceof Element)) return;

      const element = event.target.closest('a, button, [role="button"], [data-analytics-course-id]');
      if (!element || element.closest('[data-analytics-ignore="true"]')) return;

      const href = elementHref(element);
      const hrefCourseMatch = href?.match(/^\/curso\/(\d+)/);
      const courseId = element.dataset.analyticsCourseId || hrefCourseMatch?.[1];
      const documentHeight = Math.max(document.documentElement.scrollHeight, window.innerHeight);
      const label = element.getAttribute('aria-label') || element.textContent || '';

      analyticsService.track({
        event_type: courseId ? 'course_click' : 'interaction',
        course_id: courseId,
        page_path: window.location.pathname,
        source: element.dataset.analyticsSource || (courseId ? 'course_link' : 'page_action'),
        x_percent: Number(((event.clientX / Math.max(window.innerWidth, 1)) * 100).toFixed(2)),
        y_percent: Number(((event.pageY / Math.max(documentHeight, 1)) * 100).toFixed(2)),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        document_height: documentHeight,
        element_tag: element.tagName.toLowerCase(),
        element_label: label,
        element_href: href,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') analyticsService.flush();
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
};

export default AnalyticsTracker;
